import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, IconButton, Divider } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { useUtilityPanel } from '../UtilityPanelProvider'
import { useInvestigation } from '../../ResearchAgentTray/hooks/useInvestigation'
import { useActiveInvestigation } from '../../ResearchAgentTray/hooks/useActiveInvestigation'
import { useResearchAgentEnabled } from '../../ResearchAgentTray/hooks/useResearchAgentEnabled'
import { InvestigationBody, TrayFooter } from '../../ResearchAgentTray'
import SessionList from '../../ResearchAgentTray/SessionList'

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    background: 'var(--opal-surface-default, #fff)'
  },
  disabled: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-500, #6b7280)',
    fontSize: 13
  }
}))

// Mode is local to the tool because it's a presentation concern. The
// underlying investigation state (sessionId, view, polling) lives in
// `useInvestigation` and `useActiveInvestigation`.
const MODE_HISTORY = 'history'
const MODE_INVESTIGATION = 'investigation'

export default function ResearchTool ({ payload }) {
  const classes = useStyles()
  const { clientId } = useAppContext()
  const { enabled } = useResearchAgentEnabled()
  const { trackSession } = useActiveInvestigation()
  const { setHeaderMeta, registerToolActions } = useUtilityPanel()

  const [mode, setMode] = useState(MODE_HISTORY)

  const investigation = useInvestigation()
  const {
    view,
    sessionId,
    start,
    openExisting,
    answerQuestions,
    continueRun,
    isActing
  } = investigation

  // Translate the latest payload into investigation actions. Each branch
  // sets mode to 'investigation' explicitly so we don't race with the
  // sessionId update that arrives asynchronously after `start`.
  const lastPayloadRef = useRef(undefined)
  useEffect(() => {
    if (payload === lastPayloadRef.current) return
    lastPayloadRef.current = payload
    if (!payload) return

    if (payload.mode === 'start' && payload.bundle && payload.subject) {
      setMode(MODE_INVESTIGATION)
      ;(async () => {
        try {
          const newSessionId = await start(payload.bundle, payload.subject)
          if (newSessionId != null) trackSession(newSessionId)
        } catch (_) { /* surfaces via view */ }
      })()
      return
    }

    if (payload.sessionId != null) {
      openExisting(payload.sessionId)
      setMode(MODE_INVESTIGATION)
    }
  }, [payload, start, openExisting, trackSession])

  const handleSelectSession = useCallback((session) => {
    openExisting(session.portfolioResearchReportSessionId)
    setMode(MODE_INVESTIGATION)
  }, [openExisting])

  const handleBackToHistory = useCallback(() => {
    setMode(MODE_HISTORY)
  }, [])

  // Drive the panel header from the tool. The shape mirrors the old
  // TrayHeader logic so the visible text on screen doesn't change.
  useEffect(() => {
    if (mode === MODE_HISTORY) {
      setHeaderMeta({ eyebrow: 'Recent investigations', title: null, subjectLine: null })
      return
    }
    const report = view.report
    const title = (view.kind === 'complete' && report?.title) ||
      report?.report?.title ||
      report?.subject?.rowName ||
      'Selected row'
    const subjectLine = report?.subject?.view && report?.subject?.asOfDate
      ? `${report.subject.view} · as of ${report.subject.asOfDate}`
      : null
    setHeaderMeta({ eyebrow: 'Investigating', title, subjectLine })
  }, [mode, view, setHeaderMeta])

  // Register the panel-toolbar "back to history" action when relevant.
  // Pass `null` to clear so the toolbar's right group disappears in
  // history mode.
  useEffect(() => {
    if (mode === MODE_INVESTIGATION) {
      registerToolActions(
        <IconButton size='small' onClick={handleBackToHistory} aria-label='Back to history'>
          <ArrowBackIcon fontSize='small' />
        </IconButton>
      )
    } else {
      registerToolActions(null)
    }
    return () => registerToolActions(null)
  }, [mode, handleBackToHistory, registerToolActions])

  if (!enabled) {
    return (
      <div className={classes.disabled}>
        Research is not enabled for this account.
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.body}>
        {mode === MODE_HISTORY
          ? <SessionList clientId={clientId} onSelect={handleSelectSession} />
          : (
            <InvestigationBody
              view={view}
              sessionId={sessionId}
              isActing={isActing}
              onAnswerQuestions={answerQuestions}
              onContinue={continueRun}
            />
          )}
      </div>
      <Divider />
      <TrayFooter mode={mode} view={view} sessionId={sessionId} />
    </div>
  )
}

ResearchTool.propTypes = {
  payload: PropTypes.shape({
    mode: PropTypes.oneOf(['start']),
    bundle: PropTypes.object,
    subject: PropTypes.object,
    sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
}
